import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import * as moment from 'moment';
import ScreenModel from '../../../../../services/screen.model';
import {ScreenService} from '../../../../../services/screen.service';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

declare var $: any;

/**
 * @fileOverview ScreenComponent
 * @module AdminPanel/UserAccess/screen
 * @description Manages the creation, update, and listing of application screens with batch-loaded pagination.
 * Screens represent UI pages or views within the application.
 */

/**
 * @component ScreenComponent
 * @selector app-screen
 * @templateUrl ./screen.component.html
 * @styleUrls ['./screen.component.scss']
 * @implements {OnInit, OnDestroy}
 * @description Reactive form component for screen master data management (CRUD operations).
 * 
 * **Core Responsibilities:**
 * 1. Display batch-loaded list of screens (12 items per batch) with infinite scroll
 * 2. Provide reactive form for creating new screens
 * 3. Enable edit mode to update existing screens
 * 4. Search and filter screens by name and status with debouncing (300ms)
 * 5. Format timestamps for display (DD-MMM-YYYY hh:mm:ss A)
 * 6. Handle form validation with specific rules per field
 * 7. Prevent double-click submissions
 * 8. Clean up resources on component destruction
 * 
 * **Data Flow:**
 * - Fetch all screens from service on component init
 * - Initialize batch loader with 12-item batches
 * - Load first batch and display in template
 * - On scroll near bottom, automatically load next batch
 * - On search, filter screens and reset pagination
 * 
 * **Form Fields:**
 * - `name` (string): 3-20 chars, letters with optional spaces/dots, required
 * - `status` (string): Screen status indicator, required
 * 
 * **Features:**
 * - Create mode: Submit new screen with empty form
 * - Edit mode: Update existing screen from list
 * - Batch loader: 12 items per batch, infinite scroll
 * - Search: Character-normalized by name or status
 * - Timestamps: Formatted in DD-MMM-YYYY hh:mm:ss A format
 * - Double-click prevention: Disables buttons during submission
 * 
 * **Dependencies:**
 * - ScreenService: CRUD operations for screens
 * - ToastrService: Toast notifications for user feedback
 * - CommonMethodsService: Shared utility methods
 * - BatchLoader: Pagination utility for infinite scroll
 */
@Component({
  selector: 'app-screen',
  templateUrl: './screen.component.html',
  styleUrls: ['./screen.component.scss']
})
export class ScreenComponent implements OnInit, OnDestroy {
  /** @property screens @type {ScreenModel[]} @description Complete list of all screens fetched from service @public */
  public screens: ScreenModel[] = [];
  
  /** @property screensList @type {ScreenModel[]} @description Currently displayed batch of screens from batch loader @public */
  public screensList: ScreenModel[] = [];
  
  /** @property screen @type {ScreenModel} @description Current screen object for form binding (create/edit mode) @public */
  public screen: ScreenModel = new ScreenModel();
  
  /** @property filteredScreens @type {ScreenModel[]} @description Filtered list of screens from search query @public */
  public filteredScreens: ScreenModel[] = [];
  
  /** @property loginPersonType @type {string} @description User's role type from localStorage (EMPLOYEE, ADMIN, etc) @public */
  public loginPersonType: string;
  
  /** @property loginUserName @type {string} @description Logged-in user's name from localStorage @public */
  public loginUserName: string;
  
  /** @property loginUserBusinessName @type {string} @description Logged-in user's business name from localStorage @public */
  public loginUserBusinessName: string;
  
  /** @property pgLoad @type {boolean} @description Page load flag for initial data load completion @public */
  public pgLoad = false;
  
  /** @property submitButton @type {boolean} @description Flag to determine if form is in create mode (submit button visible) @public */
  public submitButton: boolean;
  
  /** @property updateButton @type {boolean} @description Flag to determine if form is in edit mode (update button visible) @public */
  public updateButton: boolean;
  
  /** @property search @type {string} @description Search query string for filtering screens @public */
  public search: string;
  
  /** @property screeenDoubleClick @type {boolean} @description Flag to prevent double-click submission on form @public */
  public screeenDoubleClick: boolean;
  
  /** @property NoDataMessage @type {boolean} @description Flag to display "no data" message when list is empty @public */
  public NoDataMessage: boolean = false;
  
  /** @property activePageNo @type {number} @description Current active page number for pagination @public */
  public activePageNo: number;
  
  /** @property clearSearchInput @type {boolean} @description Flag to clear search input field from template @public */
  public clearSearchInput: boolean = false;
  
  /** @property listLoader @type {boolean} @description Loading spinner flag for batch loader (shows during pagination load) @public */
  public listLoader = false;
  
  /** @property startIndex @type {number} @description Tracks how many items have been loaded in pagination @public */
  public startIndex = 0;
  
  /** @property isFiltering @type {boolean} @description Flag indicating if search filter is active @public */
  public isFiltering: boolean = false;
  
  /** @property noList @type {boolean} @description Flag indicating if current list is empty @public */
  public noList: boolean = false;
  
  /** @property responseLoader @type {boolean} @description Page-level loader flag for initial data load @public */
  responseLoader: boolean;
  
  /** @property searchTimeout @type {any} @description Timeout ID for debounced search (300ms) @public */
  searchTimeout: any;
  
  /** @property formFieldCount @type {number} @description Total number of form fields (used for layout calculation) @public */
  public formFieldCount: number = 0;
  
  /** @property showForm @type {boolean} @description Flag to toggle form visibility on UI @public */
  public showForm: boolean = false;
  
  /** @property registerForm @type {FormGroup} @description Reactive form group for screen creation/update with validation rules @public */
  public registerForm: FormGroup;
  
  /** @property formSubmitted @type {boolean} @description Flag to track if form has been submitted (for validation error display) @public */
  public formSubmitted: boolean = false;

  /**
   * @constructor
   * @param {ScreenService} screenService - Screen CRUD service
   * @param {Sidenavaction} sidenavaction - Navigation menu controller service
   * @param {CommonMethodsService} commonMethodsService - Shared utility methods service
   * @param {LocationStrategy} locationStrategy - Location tracking for breadcrumb
   * @param {PreviousRouteService} previousRouteService - Previous route tracking service
   * @param {FormBuilder} formBuilder - Angular reactive form builder
   * @param {ToastrService} toasterService - Toast notification service for user feedback
   * @public
   */
  constructor(public screenService: ScreenService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, public formBuilder: FormBuilder,
              public toasterService: ToastrService
  ) {
    this.screen = new ScreenModel();

    this.registerForm = this.formBuilder.group({
          name: ['', 
           [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(20),
            Validators.pattern(/^(?! )[A-Za-z]+(?:[A-Za-z\s]*[.]?[A-Za-z\s]*)?$/) 
           ]
          ],
          status: ['', Validators.required],
        })
    
        // Calculate field count after form is built
        this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /** @property batchLoader @type {BatchLoader<any>} @description Pagination utility for batch-loaded infinite scroll @private */
  private batchLoader: BatchLoader<any>;

  /**
   * @method ngOnInit
   * @async
   * @description Angular lifecycle hook. Initializes component on load with:
   * 1. Scroll page to top
   * 2. Reset all component state and lists
   * 3. Load user context from localStorage (role, name, business)
   * 4. Fetch all screens from service
   * 5. Format timestamps for display
   * 6. Initialize batch loader and load first batch
   * 
   * @public
   * @async
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.closeForm();
      this.screens = [];
      this.screensList = [];
      this.isFiltering = false;
      this.responseLoader = false;
      this.search = '';
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginUserName = localStorage.getItem('LoginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.screens = await this.screenService.getAllScreens();
      this.pgLoad = true;
      if (this.screens.length > 0) {
        this.screens.forEach(eachScreen => {
          eachScreen.updatedAt = JSON.parse(JSON.stringify(moment(eachScreen.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.responseLoader = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.responseLoader = true;
    }
  }

  /**
   * @property f
   * @getter
   * @description Getter for convenient access to form controls in template.
   * 
   * @public
   * @type {AbstractControl}
   * @returns {any} Form controls object
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Detects when user scrolls near
   * the bottom and triggers loading of next batch (50ms debounce).
   * 
   * @public
   * @hostListener window:scroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /** @property scrollTimeout @type {any} @description Timeout ID for debounced scroll handler (50ms) @private */
  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @description Checks if user scrolled near bottom and loads next batch with 50ms debounce.
   * Skips loading if already loading or if in filtering mode.
   * 
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @description Initializes batch loader for pagination (12 items per batch).
   * Binds loader to screens list and loading state.
   * 
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.screens,
      (updatedList) => {
        this.screensList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method ngOnDestroy
   * @description Angular lifecycle hook. Cancels any ongoing HTTP requests from screen service,
   * marks batch loader as destroyed, and resets batch loader state to prevent memory leaks.
   * 
   * @public
   * @returns {void}
   */
  //To cancel ongoing requests and unsubscribe from subscriptions.
  public ngOnDestroy(): void {
    this.screenService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * @method addUpdateScreen
   * @async
   * @description Creates new screen or updates existing screen. Validates form before submission,
   * prevents double-click, and reloads screen list after successful operation.
   * 
   * **Create Mode:** Submits new screen with form values.
   * **Edit Mode:** Updates existing screen without submitting ID, clones object for safety.
   * 
   * @public
   * @async
   * @returns {Promise<void | boolean>}
   */
  /*add feature*/
  public async addUpdateScreen() {
    this.formSubmitted = true;
    if (this.registerForm.invalid) return false;
    this.formSubmitted = false;
    try {
      this.screeenDoubleClick = true;
      if (this.submitButton) {
        this.screen = await this.screenService.create(this.screen);
        this.toasterService.success('Screen added successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateScreen = Object.assign({}, this.screen);
        delete updateScreen.id;
        this.screen = await this.screenService.updateOneByID(this.screen.id, updateScreen);
        this.toasterService.success('Screen updated successfully', '', {
            timeOut: 2000
          });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.screeenDoubleClick = false;
    }
  }

  /**
   * @method getScreenById
   * @description Retrieves screen by ID from current list and populates reactive form with data.
   * Used for edit mode to pre-fill form with existing screen values.
   * 
   * @public
   * @param {string} id - The unique identifier of screen to retrieve
   * @returns {void}
   */
  /*Get screen by id*/
  public getScreenById(id: string) {
    this.screeenDoubleClick = false;
    this.updateButton = true;
    this.submitButton = false;
    this.showForm = true;
    const foundScreen = this.screensList.find(screen => screen?.id === id);
    this.screen = foundScreen ? { ...foundScreen } : new ScreenModel(); // clone object
  }

  /**
   * @method applySearch
   * @description Applies search filter to screens by name and status.
   * Removes spaces and hyphens for character-normalized matching.
   * Resets pagination and loads filtered results.
   * 
   * @public
   * @returns {void}
   */
  public applySearch(): void {
    this.screensList = [];
    const query = this.search?.replace(/[-_]|\s/g,"").trim().toLowerCase();
    this.isFiltering = query.length > 0;

    if (this.isFiltering) {
      this.screensList = this.screens.filter(screen =>
        screen.name?.replace(/[-_]|\s/g,"").toLowerCase().includes(query) ||
        screen.status?.replace(/[-_]|\s/g,"").toLowerCase().includes(query)
      )
    } else {
       this.initializeBatchLoader();
       this.batchLoader?.reset();
       this.batchLoader?.loadNextBatch(true);
    }

    this.noList = this.screensList.length === 0;
  }

  /**
   * @method openForm
   * @description Initializes create mode. Sets form state flags to show submit button,
   * displays form for new screen creation.
   * 
   * @public
   * @returns {void}
   */
  /*handle update and submit buttons*/
  public openForm() {
    this.submitButton = true;
    this.updateButton = false;
    this.showForm = true;
  }

  /**
   * @method closeForm
   * @description Closes screen form and resets all form-related state. Called on Cancel button
   * or after successful submission.
   * 
   * @public
   * @returns {void}
   */
  /*click on cancel button form data empty reload the page*/
  public closeForm() {
    this.registerForm.reset();
    this.showForm = false;
    this.screen = new ScreenModel();
    this.screeenDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
  }
}
